import cloneDeep from 'clone-deep';
import deepmerge from 'deepmerge';

import {
  IStatement,
  MapStatementData,
  StatementType,
  StepName,
} from '@/types/services';
import { isMrpaEnable } from '@/app.config';
import {
  AcceptanceDepositStep,
  CorrectTranslationStep,
  DocumentConversionStep,
  EgrnConfirmStep,
  evidenceDocTypeToDocumentName,
  ExtractionRodStep,
  NoticeRecoveryStep,
  RodStep,
  RuzdiStep,
  SecuringEvidenceStep,
  StorageAcceptStep,
  StorageIssueStep,
  TransferElDocsStep,
} from '@/constants';

type SubjectFillTypeHandlerParams = {
  statement: IStatement;
  data: MapStatementData;
  subjectName: 'applicant' | 'applicantRightholder' | 'debtor';
  nextStepName: 'step2' | 'step4';
};

const subjectFillTypeHandler = ({
  statement,
  data,
  subjectName,
  nextStepName,
}: SubjectFillTypeHandlerParams) => {
  const statementApplicantFillType =
    statement.subjects[subjectName]?.uiContext?.fillType;

  // очищаем applicant если изменился fillType
  if (
    statementApplicantFillType &&
    statementApplicantFillType !== data.fragment
  ) {
    statement.subjects[subjectName] = {};

    const index =
      statement.stepsCompleted &&
      statement.stepsCompleted.findIndex((step) => step === nextStepName);

    if (index && index > -1 && statement.stepsCompleted) {
      statement.stepsCompleted.splice(index, 1);
    }
  }

  // выставляем fillType
  statement.subjects[subjectName] = deepmerge(
    statement.subjects[subjectName] ?? {},
    {
      uiContext: {
        fillType: data.fragment,
      },
    }
  );
};

const mrpaSelectApplicantHandler = (
  statement: IStatement,
  data: MapStatementData,
  statementType: string
) => {
  if (isMrpaEnable(statementType) && data.step === StepName.S1) {
    if (statement.applicantRole !== data.fragment) {
      statement.subjects.applicant = {};

      const index =
        statement.stepsCompleted &&
        statement.stepsCompleted.findIndex((step) => step === 'step2');

      if (index && index > -1 && statement.stepsCompleted) {
        statement.stepsCompleted.splice(index, 1);
      }
    }

    statement.applicantRole = data.fragment;
  }
};

const statementTypeToMapStatement = {
  [StatementType.NOTICE_RECOVERY]: (
    statement: IStatement,
    data: MapStatementData
  ) => {
    if (
      isMrpaEnable(StatementType.NOTICE_RECOVERY) &&
      data.step === StepName.S1
    ) {
      subjectFillTypeHandler({
        statement,
        data,
        subjectName: 'applicant',
        nextStepName: 'step2',
      });
      return;
    }

    if (data.step === NoticeRecoveryStep.APPLICANT) {
      statement.subjects.applicant = deepmerge(data.fragment, {
        uiContext: statement.subjects.applicant?.uiContext,
      });
      return;
    }

    if (data.step === NoticeRecoveryStep.CREDITORS) {
      statement.subjects.previousCreditors = cloneDeep(
        data.fragment.previousCreditors
      );
      statement.prevCreditorDocuments = cloneDeep(
        data.fragment.prevCreditorDocuments
      );
      return;
    }

    if (data.step === NoticeRecoveryStep.DEBTOR) {
      statement.subjects.debtor = cloneDeep(data.fragment);
      return;
    }

    if (data.step === NoticeRecoveryStep.DEBTORS) {
      statement.subjects.previousDebtors = cloneDeep(
        data.fragment.previousDebtors
      );
      statement.prevDebtorDocuments = cloneDeep(
        data.fragment.prevDebtorDocuments
      );
      return;
    }

    if (data.step === NoticeRecoveryStep.RECOVERY_DETAILS) {
      statement.executiveNoticesRecoveryDetails = {
        ...statement.executiveNoticesRecoveryDetails,
        ...cloneDeep(data.fragment.executiveNoticesRecoveryDetails),
      };
      statement.notarialDocuments = cloneDeep(data.fragment.notarialDocuments);
      return;
    }

    if (data.step === NoticeRecoveryStep.JUDGEMENT_CREDITOR_BANK_DETAILS) {
      statement.executiveNoticesRecoveryDetails = {
        ...statement.executiveNoticesRecoveryDetails,
        ...cloneDeep(data.fragment.executiveNoticesRecoveryDetails),
      };
      statement.judgementCreditorBankDetails = {
        ...statement.judgementCreditorBankDetails,
        ...cloneDeep(data.fragment.judgementCreditorBankDetails),
      };
      statement.statementParamDocuments = cloneDeep(
        data.fragment.statementParamDocuments
      );

      return;
    }

    if (data.step === NoticeRecoveryStep.ACCOUNT_BANK_DETAILS) {
      statement.executiveNoticesRecoveryDetails = {
        ...statement.executiveNoticesRecoveryDetails,
        ...cloneDeep(data.fragment.executiveNoticesRecoveryDetails),
      };
      statement.accountBankDetails = {
        ...statement.accountBankDetails,
        ...cloneDeep(data.fragment.accountBankDetails),
      };

      return;
    }

    if (data.step === StepName.DOCUMENTS) {
      statement.documents = cloneDeep(data.fragment);
    }
  },

  [StatementType.RUZDI]: (statement: IStatement, data: MapStatementData) => {
    if (isMrpaEnable(StatementType.RUZDI) && data.step === StepName.S1) {
      subjectFillTypeHandler({
        statement,
        data,
        subjectName: 'applicant',
        nextStepName: 'step2',
      });
      return;
    }

    if (data.step === RuzdiStep.APPLICANT) {
      statement.subjects.applicant = deepmerge(data.fragment, {
        uiContext: statement.subjects.applicant?.uiContext,
      });
      return;
    }

    if (data.step === RuzdiStep.EXTRACT_TYPE) {
      statement.ruzdiDetails = {
        ...cloneDeep(statement.ruzdiDetails),
        extractType: data.fragment.extractType,
      };
      // @ts-ignore
      statement.subjects.applicant.role = data.fragment.role;

      return;
    }

    if (data.step === RuzdiStep.PLEDGOR) {
      statement.subjects.pledgor = cloneDeep(data.fragment.pledgor);
      statement.ruzdiDetails = deepmerge(
        statement.ruzdiDetails ?? {},
        data.fragment.ruzdiDetails
      );
      return;
    }

    if (data.step === RuzdiStep.BANK_DETAILS) {
      statement.accountBankDetails = {
        ...statement.accountBankDetails,
        ...cloneDeep(data.fragment.accountBankDetails),
      };
      statement.ruzdiDetails = {
        ...statement.ruzdiDetails,
        ...cloneDeep(data.fragment.ruzdiDetails),
      };
      statement.documents = cloneDeep(data.fragment.documents);
    }
  },

  [StatementType.SECURING_EVIDENCE]: (
    statement: IStatement,
    data: MapStatementData
  ) => {
    if (
      isMrpaEnable(StatementType.SECURING_EVIDENCE) &&
      data.step === StepName.S1
    ) {
      subjectFillTypeHandler({
        statement,
        data,
        subjectName: 'applicant',
        nextStepName: 'step2',
      });
      return;
    }

    if (data.step === SecuringEvidenceStep.APPLICANT) {
      statement.subjects.applicant = deepmerge(data.fragment, {
        uiContext: statement.subjects.applicant?.uiContext,
      });
      return;
    }

    if (data.step === SecuringEvidenceStep.URL) {
      statement.securityEvidenceDetails = deepmerge(
        statement.securityEvidenceDetails ?? {},
        data.fragment
      );
      return;
    }

    if (data.step === SecuringEvidenceStep.NOTIFICANTS) {
      statement.subjects.notificants = data.fragment.notificants;
      statement.securityEvidenceDetails = deepmerge(
        statement.securityEvidenceDetails ?? {},
        data.fragment.securityEvidenceDetails
      );
    }

    if (data.step === SecuringEvidenceStep.BANK_DETAILS) {
      statement.securityEvidenceDetails = {
        ...statement.securityEvidenceDetails,
        ...data.fragment.securityEvidenceDetails,
      };
      statement.accountBankDetails = {
        ...statement.accountBankDetails,
        ...data.fragment.accountBankDetails,
      };
      return;
    }

    if (data.step === StepName.DOCUMENTS) {
      statement.documents = cloneDeep(data.fragment);
      return;
    }
  },

  [StatementType.ACCEPTANCE_DEPOSIT]: (
    statement: IStatement,
    data: MapStatementData
  ) => {
    if (
      isMrpaEnable(StatementType.ACCEPTANCE_DEPOSIT) &&
      data.step === StepName.S1
    ) {
      subjectFillTypeHandler({
        statement,
        data,
        subjectName: 'debtor',
        nextStepName: 'step2',
      });
      return;
    }

    if (data.step === AcceptanceDepositStep.DEBTOR) {
      statement.subjects.debtor = deepmerge(data.fragment, {
        uiContext: statement.subjects.debtor?.uiContext,
      });
      return;
    }

    if (data.step === AcceptanceDepositStep.CREDITORS) {
      statement.subjects.creditors = cloneDeep(data.fragment.creditors);
      statement.acceptanceDepositDetails = deepmerge(
        statement.acceptanceDepositDetails || {},
        data.fragment.acceptanceDepositDetails
      );
      return;
    }

    if (data.step === AcceptanceDepositStep.DEPOSIT_DETAILS) {
      statement.obligationDocument = cloneDeep(
        data.fragment.obligationDocument
      );
      statement.acceptanceDepositDetails = deepmerge(
        statement.acceptanceDepositDetails || {},
        data.fragment.acceptanceDepositDetails
      );
      return;
    }

    if (data.step === AcceptanceDepositStep.DEBTOR_BANK_DETAILS) {
      statement.debtorAccountBankDetails = cloneDeep(data.fragment);
      return;
    }

    if (data.step === AcceptanceDepositStep.APPLICANT_BANK_DETAILS) {
      statement.applicantAccountBankDetails = cloneDeep(
        data.fragment.applicantAccountBankDetails
      );
      statement.acceptanceDepositDetails = deepmerge(
        statement.acceptanceDepositDetails || {},
        data.fragment.acceptanceDepositDetails
      );
      return;
    }

    if (data.step === StepName.DOCUMENTS) {
      statement.documents = cloneDeep(data.fragment);
    }
  },

  [StatementType.TRANSFER_EL_DOCS]: (
    statement: IStatement,
    data: MapStatementData
  ) => {
    if (
      isMrpaEnable(StatementType.TRANSFER_EL_DOCS) &&
      data.step === StepName.S1
    ) {
      subjectFillTypeHandler({
        statement,
        data,
        subjectName: 'applicant',
        nextStepName: 'step2',
      });
      return;
    }

    if (data.step === TransferElDocsStep.APPLICANT) {
      statement.subjects.applicant = deepmerge(data.fragment, {
        uiContext: statement.subjects.applicant?.uiContext,
      });
      return;
    }

    if (data.step === TransferElDocsStep.ADDRESSEE) {
      statement.subjects.addressee = cloneDeep(data.fragment.addressee);
      statement.transferDocuments = cloneDeep(data.fragment.transferDocuments);
      return;
    }

    if (data.step === TransferElDocsStep.BANK_DETAILS) {
      statement.accountBankDetails = deepmerge(
        statement.accountBankDetails ?? {},
        data.fragment.accountBankDetails
      );
      statement.transferElDocsDetails = deepmerge(
        statement.transferElDocsDetails ?? {},
        data.fragment.transferElDocsDetails
      );
      return;
    }

    if (data.step === StepName.DOCUMENTS) {
      statement.documents = cloneDeep(data.fragment);
    }
  },

  [StatementType.CORRECT_TRANSLATION]: (
    statement: IStatement,
    data: MapStatementData
  ) => {
    if (
      isMrpaEnable(StatementType.CORRECT_TRANSLATION) &&
      data.step === StepName.S1
    ) {
      subjectFillTypeHandler({
        statement,
        data,
        subjectName: 'applicant',
        nextStepName: 'step2',
      });
      return;
    }

    if (data.step === CorrectTranslationStep.APPLICANT) {
      statement.subjects.applicant = deepmerge(data.fragment, {
        uiContext: statement.subjects.applicant?.uiContext,
      });
      return;
    }

    if (data.step === CorrectTranslationStep.TRANSLATED_DOCUMENT) {
      statement.translatedDocument = cloneDeep(
        data.fragment.translatedDocument
      );
      statement.correctTranslationDetails = deepmerge(
        statement.correctTranslationDetails ?? {},
        data.fragment.correctTranslationDetails
      );
      return;
    }

    if (data.step === CorrectTranslationStep.BANK_DETAILS) {
      statement.accountBankDetails = cloneDeep(
        data.fragment.accountBankDetails
      );
      statement.correctTranslationDetails = deepmerge(
        statement.correctTranslationDetails ?? {},
        data.fragment.correctTranslationDetails
      );
      return;
    }

    if (data.step === StepName.DOCUMENTS) {
      statement.documents = cloneDeep(data.fragment);
    }
  },

  [StatementType.ACCEPTANCE_ESCROW]: (
    statement: IStatement,
    data: MapStatementData
  ) => {
    if (data.step === StepName.S1) {
      statement.subjects.debtors = cloneDeep(data.fragment);
    }

    if (data.step === StepName.S2) {
      statement.subjects.creditors = cloneDeep(data.fragment);
    }

    if (data.step === StepName.S3) {
      statement.acceptanceEscrowDetails = deepmerge(
        //@ts-ignore
        statement.acceptanceEscrowDetails,
        data.fragment
      );
    }

    if (data.step === StepName.S4) {
      statement.debtorAccountBankDetails = cloneDeep(data.fragment);
    }

    if (data.step === StepName.S5) {
      statement.applicantAccountBankDetails = cloneDeep(
        data.fragment.applicantAccountBankDetails
      );
      statement.acceptanceEscrowDetails = deepmerge(
        //@ts-ignore
        statement.acceptanceEscrowDetails,
        data.fragment.acceptanceEscrowDetails
      );
    }

    if (data.step === StepName.DOCUMENTS) {
      statement.documents = cloneDeep(data.fragment);
    }
  },

  [StatementType.STORAGE_ACCEPT]: (
    statement: IStatement,
    data: MapStatementData
  ) => {
    if (
      isMrpaEnable(StatementType.STORAGE_ACCEPT) &&
      data.step === StepName.S1
    ) {
      subjectFillTypeHandler({
        statement,
        data,
        subjectName: 'applicant',
        nextStepName: 'step2',
      });
      return;
    }

    if (data.step === StorageAcceptStep.APPLICANT) {
      statement.subjects.applicant = deepmerge(data.fragment, {
        uiContext: statement.subjects.applicant?.uiContext,
      });
      return;
    }

    if (data.step === StorageAcceptStep.STORAGE_ACCEPT_DETAILS) {
      statement.storageAcceptDetails = cloneDeep(
        data.fragment.storageAcceptDetails
      );
      statement.storageDocument = cloneDeep(data.fragment.storageDocument);
      return;
    }

    if (data.step === StorageAcceptStep.BANK_DETAILS) {
      statement.accountBankDetails = cloneDeep(
        data.fragment.accountBankDetails
      );
      statement.storageAcceptDetails = cloneDeep(
        data.fragment.storageAcceptDetails
      );
      return;
    }

    if (data.step === StepName.DOCUMENTS) {
      statement.documents = cloneDeep(data.fragment);
    }
  },

  [StatementType.DOCUMENT_CONVERSION]: (
    statement: IStatement,
    data: MapStatementData
  ) => {
    if (
      isMrpaEnable(StatementType.DOCUMENT_CONVERSION) &&
      data.step === StepName.S1
    ) {
      subjectFillTypeHandler({
        statement,
        data,
        subjectName: 'applicant',
        nextStepName: 'step2',
      });
      return;
    }

    if (data.step === DocumentConversionStep.APPLICANT) {
      statement.subjects.applicant = deepmerge(data.fragment, {
        uiContext: statement.subjects.applicant?.uiContext,
      });
      return;
    }

    if (data.step === DocumentConversionStep.CONVERSION_DOCUMENT) {
      statement.conversionDocument = cloneDeep(data.fragment);
      return;
    }

    if (data.step === DocumentConversionStep.DOCUMENT_CONVERSION_DETAILS) {
      statement.documentConversionDetails = deepmerge(
        statement.documentConversionDetails ?? {},
        data.fragment
      );
      return;
    }

    if (data.step === DocumentConversionStep.BANK_DETAILS) {
      statement.documentConversionDetails = {
        ...statement.documentConversionDetails,
        ...data.fragment.documentConversionDetails,
      };
      statement.accountBankDetails = {
        ...statement.accountBankDetails,
        ...data.fragment.accountBankDetails,
      };
      return;
    }

    if (data.step === StepName.DOCUMENTS) {
      statement.documents = cloneDeep(data.fragment);
    }
  },
  [StatementType.STORAGE_ISSUE]: (
    statement: IStatement,
    data: MapStatementData
  ) => {
    if (
      isMrpaEnable(StatementType.STORAGE_ISSUE) &&
      data.step === StepName.S1
    ) {
      subjectFillTypeHandler({
        statement,
        data,
        subjectName: 'applicant',
        nextStepName: 'step2',
      });
      return;
    }

    if (data.step === StorageIssueStep.APPLICANT) {
      statement.subjects.applicant = deepmerge(data.fragment, {
        uiContext: statement.subjects.applicant?.uiContext,
      });
      return;
    }

    if (data.step === StorageIssueStep.STORAGE_FILE) {
      statement.storageIssueDetails = deepmerge(
        statement.storageIssueDetails ?? {},
        data.fragment.storageIssueDetails
      );
      return;
    }

    if (data.step === StorageIssueStep.BANK_DETAILS) {
      statement.accountBankDetails = cloneDeep(
        data.fragment.accountBankDetails
      );
      statement.storageIssueDetails = deepmerge(
        statement.storageIssueDetails ?? {},
        data.fragment.storageIssueDetails
      );
      return;
    }
    if (data.step === StepName.DOCUMENTS) {
      statement.documents = cloneDeep(data.fragment);
    }
  },

  [StatementType.ROD]: (statement: IStatement, data: MapStatementData) => {
    mrpaSelectApplicantHandler(statement, data, StatementType.ROD);

    if (data.step === RodStep.APPLICANT) {
      statement.subjects.applicant = cloneDeep(data.fragment);

      if (!isMrpaEnable(StatementType.ROD)) {
        statement.applicantRole = undefined;
      }
    }

    if (data.step === RodStep.PROXIES) {
      statement.rodDetails = {
        proxies: cloneDeep(data.fragment),
      };
    }

    if (data.step === StepName.DOCUMENTS) {
      statement.documents = cloneDeep(data.fragment);
    }
  },

  [StatementType.EXTRACTION_ROD]: (
    statement: IStatement,
    data: MapStatementData
  ) => {
    mrpaSelectApplicantHandler(statement, data, StatementType.EXTRACTION_ROD);

    if (data.step === ExtractionRodStep.APPLICANT) {
      statement.subjects.applicant = cloneDeep(data.fragment);

      if (!isMrpaEnable(StatementType.EXTRACTION_ROD)) {
        statement.applicantRole = undefined;
      }
    }

    if (data.step === ExtractionRodStep.PROXY_INFO) {
      statement.extractionRodDetails = {
        ...statement.extractionRodDetails,
        ...cloneDeep(data.fragment.extractionRodDetails),
      };
    }

    if (data.step === ExtractionRodStep.BANK_DETAILS) {
      statement.extractionRodDetails = {
        ...statement.extractionRodDetails,
        ...data.fragment.extractionRodDetails,
      };
      statement.accountBankDetails = {
        ...statement.accountBankDetails,
        ...data.fragment.accountBankDetails,
      };
    }

    if (data.step === StepName.DOCUMENTS) {
      statement.documents = cloneDeep(data.fragment);
    }
  },

  [StatementType.EGRN_CONFIRM]: (
    statement: IStatement,
    data: MapStatementData
  ) => {
    if (isMrpaEnable(StatementType.EGRN_CONFIRM) && data.step === StepName.S1) {
      subjectFillTypeHandler({
        statement,
        data,
        subjectName: 'applicant',
        nextStepName: 'step2',
      });
      return;
    }

    if (data.step === EgrnConfirmStep.APPLICANT) {
      if (data.step === StepName.S1) {
        statement.subjects.applicant = data.fragment;
        return;
      }

      statement.subjects.applicant = deepmerge(data.fragment, {
        uiContext: statement.subjects.applicant?.uiContext,
      });
      return;
    }

    if (isMrpaEnable(StatementType.EGRN_CONFIRM) && data.step === StepName.S3) {
      if (data.fragment) {
        subjectFillTypeHandler({
          statement,
          data,
          subjectName: 'applicantRightholder',
          nextStepName: 'step4',
        });
      } else {
        statement.subjects.applicantRightholder = undefined;
      }

      return;
    }

    if (data.step === EgrnConfirmStep.APPLICANT_RIGHTHOLDER) {
      if (EgrnConfirmStep.APPLICANT_RIGHTHOLDER === StepName.S2) {
        if (data.fragment) {
          statement.subjects.applicantRightholder = cloneDeep(data.fragment);
        } else {
          statement.subjects.applicantRightholder = undefined;
        }
      } else {
        if (data.fragment) {
          statement.subjects.applicantRightholder = deepmerge(data.fragment, {
            uiContext: statement.subjects.applicantRightholder?.uiContext,
          });
        }
      }

      return;
    }

    if (data.step === EgrnConfirmStep.ESTATE_OBJECT) {
      statement.egrnConfirmDetails = {
        ...statement.egrnConfirmDetails,
        ...data.fragment,
      };

      return;
    }

    if (data.step === EgrnConfirmStep.PROOF) {
      if (statement.subjects.applicantRightholder) {
        statement.evidenceDocType = undefined;
        statement.contractDoc = undefined;
        statement.preliminaryContractDoc = undefined;
        statement.protectionPrivatizationDoc = undefined;
        statement.compensationDamageDoc = undefined;
        statement.lawsuitRightHolderDoc = undefined;
        statement.foreclosureEstateRightholderDoc = undefined;
        statement.otherRightholder = undefined;

        if (statement.egrnConfirmDetails) {
          statement.egrnConfirmDetails.dealType = data.fragment;
        }
      } else {
        if (statement.egrnConfirmDetails) {
          statement.egrnConfirmDetails.dealType = null;
        }

        statement.evidenceDocType = data.fragment.docType;
        // @ts-ignore
        statement[evidenceDocTypeToDocumentName[data.fragment.docType]] =
          data.fragment.document;
      }

      return;
    }

    if (data.step === EgrnConfirmStep.REFUND_DETAILS) {
      statement.accountBankDetails = cloneDeep(
        data.fragment.accountBankDetails
      );
      statement.egrnConfirmDetails = deepmerge(
        statement.egrnConfirmDetails || {},
        data.fragment.egrnConfirmDetails
      );

      return;
    }

    if (data.step === StepName.DOCUMENTS) {
      statement.documents = cloneDeep(data.fragment);
    }
  },
};

export default statementTypeToMapStatement;
